import React from 'react';
import Icon from '../../../components/AppIcon';


const ProjectCard = ({ project, onClick, onEdit, onDelete, getStatusColor }) => {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getStatusLabel = (status) => {
    return status?.charAt(0)?.toUpperCase() + status?.slice(1)?.replace('-', ' ');
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return 'Play';
      case 'planning':
        return 'FileText';
      case 'completed':
        return 'CheckCircle';
      case 'on-hold':
        return 'Pause';
      default:
        return 'Circle';
    }
  };

  return (
    <div className="bg-card rounded-lg border border-border hover:border-primary transition-all duration-250 cursor-pointer hover:shadow-md group w-[280px] h-[380px] flex flex-col">
      <div className="p-3 flex flex-col h-full" onClick={onClick}>
        {/* Header with Image and Project Name */}
        <div className="flex items-start gap-2 mb-2">
          {/* Project Image - Fixed size */}
          {project?.imageUrl && (
            <div className="w-20 h-20 rounded overflow-hidden border border-border shrink-0">
              <img
                src={project?.imageUrl}
                alt={project?.name}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
          )}
          <div className="flex-1 min-w-0">
            <h3 className="text-sm font-semibold text-foreground mb-1 line-clamp-2 group-hover:text-primary transition-colors">
              {project?.name}
            </h3>
            {project?.projectNo && (
              <p className="text-xs text-muted-foreground mb-1">
                {project?.projectNo}
              </p>
            )}
            {/* Status Tag */}
            <div
              className="flex items-center gap-1 px-2 py-0.5 rounded w-fit"
              style={{ backgroundColor: `${getStatusColor(project?.status)}20` }}
            >
              <Icon
                name={getStatusIcon(project?.status)}
                size={12}
                color={getStatusColor(project?.status)}
              />
              <span
                className="text-xs font-medium"
                style={{ color: getStatusColor(project?.status) }}
              >
                {getStatusLabel(project?.status)}
              </span>
            </div>
          </div>
        </div>

        {/* Spacer to push team/owner/progress to bottom */}
        <div className="flex-1" />

        {/* Client/Owner (Left) and Country/Type (Right) - Bottom aligned */}
        <div className="mb-2">
          <div className="flex items-start justify-between gap-3">
            {/* Client and Owner - Left aligned */}
            <div className="flex-1 min-w-0">
              {/* Client */}
              {project?.client && (
                <div className="mb-1.5">
                  <span className="text-xs font-bold text-muted-foreground block mb-0.5">Client</span>
                  <div className="text-xs text-foreground truncate">
                    {project?.client}
                  </div>
                </div>
              )}
              
              {/* Owner */}
              <div>
                <span className="text-xs font-bold text-muted-foreground block mb-0.5">Owner</span>
                <div className="text-xs text-foreground truncate">
                  {project?.owner}
                </div>
              </div>
            </div>

            {/* Project Type and Country - Right side */}
            <div className="flex-1 min-w-0">
              {/* Project Type */}
              {project?.projectType && (
                <div className="mb-1.5">
                  <span className="text-xs font-bold text-muted-foreground block mb-0.5">Type</span>
                  <div className="text-xs text-foreground truncate">
                    {project?.projectType}
                  </div>
                </div>
              )}
              
              {/* Project Country */}
              {project?.projectCountry && (
                <div>
                  <span className="text-xs font-bold text-muted-foreground block mb-0.5">Country</span>
                  <div className="text-xs text-foreground truncate">
                    {project?.projectCountry}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mb-3">
          <div className="flex items-center justify-between mb-1">
            <span className="text-xs font-medium text-muted-foreground">Progress</span>
            <span className="text-xs font-semibold text-foreground">{project?.progress}%</span>
          </div>
          <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden">
            <div
              className="h-full bg-primary rounded-full transition-all duration-500"
              style={{ width: `${project?.progress}%` }}
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between pt-2 border-t border-border mt-auto">
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <div className="flex items-center gap-1">
              <Icon name="Workflow" size={14} />
              <span>{project?.workflowCount}</span>
            </div>
            <div className="flex items-center gap-1">
              <Icon name="Clock" size={14} />
              <span>{formatDate(project?.lastModified)}</span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              className="p-1 hover:bg-muted rounded transition-colors"
              onClick={(e) => {
                e?.stopPropagation();
                onEdit();
              }}
            >
              <Icon name="Edit" size={16} />
            </button>
            <button
              className="p-1 hover:bg-destructive/10 rounded transition-colors text-destructive"
              onClick={(e) => {
                e?.stopPropagation();
                onDelete();
              }}
            >
              <Icon name="Trash2" size={16} />
            </button>
            <button
              className="p-1 hover:bg-muted rounded transition-colors"
              onClick={(e) => {
                e?.stopPropagation();
                onClick();
              }}
            >
              <Icon name="ArrowRight" size={16} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;